import type {
  ComputedGridConfig,
  GridChangeEvent,
  GridDataItemRaw,
  GridDataItem,
} from "@/types/grid";
import type {
  AppendDataFn,
  BulkImportChanges,
  ColumnMappingSuggestion,
  UploadedData,
} from "@/types/bulk-import";

/**
 * This composable is a wrapper around useBulkImportStore and useImportFacilityApi
 */
export function useImportPage() {
  const { t, getAllTranslations, formatOptions } = useFormatOptions();
  const router = useRouter();
  const bulkImportStore = useBulkImportStore();
  const {
    activeImportFacilityId,
    importPagesList,
    updateFacilityStatus,
    getPageDraftSnapshot,
    setPageDraftSnapshot,
    clearPageDraftSnapshot,
  } = useImportFacilityApi();
  const { handleSafeExecution } = useSafeExecution();
  const uploadImportFile = useUploadImportFile();

  const {
    currentPageSetupId,
    gridConfig,
    dataItems,
    deletedItems,
    isMappingPending,
    mappingSuggestions,
    uploadedData,
    uploadedDataColumns,
    hasMergeConflicts,
    mergeConflicts,
    unsavedChangesState,
  } = storeToRefs(bulkImportStore);

  const currentPageSetup = computed(() => {
    if (!currentPageSetupId.value) return;
    return importPagesList.value.find(
      (page) => page.id === currentPageSetupId.value,
    );
  });

  const currentDraftSnapshot = computed(() =>
    currentPageSetupId.value !== undefined
      ? getPageDraftSnapshot(currentPageSetupId.value)
      : undefined,
  );

  const hasDraftSnapshot = computed(
    () => currentDraftSnapshot.value !== undefined,
  );

  const hasUnsavedChanges = computed(
    () => bulkImportStore.unsavedChangesState.hasUnsavedChanges,
  );

  const isDataStale = computed(
    () => hasDraftSnapshot.value || hasUnsavedChanges.value,
  );

  const originalDataItems = computed(() =>
    currentPageSetup.value ? toValue(currentPageSetup.value.dataItems) : [],
  );

  const templateFileName = computed(
    () =>
      currentPageSetup.value?.templateFileName ||
      "cksetup.imports.import-into-table.templateName",
  );

  /**
   * Goes on the main app header
   */
  const pageTitle = computed(() => currentPageSetup.value?.pageTitle || "");

  /**
   * Go in the h1 of the specific bulk import page for the current import
   */
  const pageHeading = computed(() => currentPageSetup.value?.pageHeading || "");

  const validationSchema = computed(
    () => currentPageSetup.value?.validationSchema,
  );

  const mappableColumnsHash = computed<
    ComputedGridConfig["mappableColumnsHash"]
  >(() => gridConfig.value?.mappableColumnsHash ?? new Map());

  const importableColumnsKeys = computed(() => [
    ...(gridConfig.value?.importableColumnsHash?.keys() ?? []),
  ]);

  const mappableColumns = computed(() => [
    ...mappableColumnsHash.value.values(),
  ]);

  const showLeaveConfirmationDialog = computed(
    () => unsavedChangesState.value.showLeaveConfirmation,
  );

  const prepareItemsForEdition = (originalItems: GridDataItem[]) => {
    return computeAndFormatItems(originalItems, {
      gridConfig: gridConfig.value,
    });
  };

  /**
   * Map against the updated facility and related data to fix possible errors
   * @param changes
   * @returns
   */
  const normalizeMemoryChanges = async (
    changes: BulkImportChanges,
  ): Promise<BulkImportChanges> => {
    if (!gridConfig.value) return changes;

    return {
      ...changes,
      addedItems: await transformDataItems(
        changes.addedItems,
        gridConfig.value,
        formatOptions.value,
        currentPageSetup.value?.transformDataItemFn,
      ),
      updatedItems: await transformDataItems(
        changes.updatedItems,
        gridConfig.value,
        formatOptions.value,
        currentPageSetup.value?.transformDataItemFn,
      ),
    };
  };

  const setDataItems = async (
    originalItems: GridDataItem[],
    memoryChanges?: BulkImportChanges,
  ) => {
    let gridDataItems = originalItems;

    deletedItems.value = [];

    // Check if there are in memory pending changes
    if (memoryChanges) {
      const normalizedChanges = await normalizeMemoryChanges(memoryChanges);
      const result = applyDraftSnapshot(
        normalizedChanges,
        gridDataItems,
        gridConfig.value?.uniqueKey,
      );

      gridDataItems = result.dataItems;
      deletedItems.value = result.deletedItems as GridDataItem[];
    }

    dataItems.value = await prepareItemsForEdition(gridDataItems);
  };

  const load = async () => {
    await handleSafeExecution(
      async () => {
        // Prevent reloading if page is not initialized or the gridConfig is already loaded
        if (!currentPageSetup.value || gridConfig.value) {
          return;
        }
        gridConfig.value = computeGridConfig(
          await currentPageSetup.value.loadGridConfigFn(),
          {
            validationSchema: validationSchema.value,
            formatOptions,
          },
        );

        await setDataItems(
          clonePersistentItems(originalDataItems.value),
          currentDraftSnapshot.value,
        );

        const response = await currentPageSetup.value.loadGridDataFn();

        await setDataItems(
          clonePersistentItems(response),
          currentDraftSnapshot.value,
        );
      },
      {
        showGlobalLoadingIndicator: true,
      },
    );
  };

  /**
   * Restore the data to the original data items called on init or on load.
   */
  const restoreOriginal = async () => {
    if (currentPageSetupId.value) {
      clearPageDraftSnapshot(currentPageSetupId.value);
    }

    bulkImportStore.deletedItems = [];
    bulkImportStore.mappingSuggestions = [];
    bulkImportStore.uploadedData = [];
    bulkImportStore.uploadedDataColumns = [];
    bulkImportStore.mergeConflicts = [];
    unsavedChangesState.value.hasUnsavedChanges = false;
    unsavedChangesState.value.showLeaveConfirmation = false;
    unsavedChangesState.value.confirmationLeaveRoute = undefined;

    await setDataItems(clonePersistentItems(originalDataItems.value));
  };

  /**
   * Downloads a template file.
   * @returns {Promise<File>}
   */
  const downloadTemplate = async () => {
    if (!currentPageSetup.value) {
      devConsole.error("No page setup available");
      return;
    }
    try {
      const binaryBlob = await currentPageSetup.value?.downloadTemplateFn();
      if (!binaryBlob) return;

      const templateName = t(templateFileName.value);
      const fileName = `${templateName}.xlsx`;

      return new File([binaryBlob], fileName);
    } catch (e) {
      devConsole.error("error downloading template", e);
    }
  };

  /**
   * Uploads a file and maps its columns to the grid configuration.
   * If will always return the uploaded data, even if the mapping is not complete.
   * The computed isMappingPending can be used to check if the mapping is complete.
   *
   */
  const uploadAndMap = async (
    file: File,
  ): Promise<UploadedData | undefined> => {
    if (
      !currentPageSetup.value ||
      !gridConfig.value ||
      !activeImportFacilityId.value
    ) {
      devConsole.error("No page setup available");
      return;
    }

    // TODO (JM): make a feature flag for this
    // save the file on server for latter validation
    uploadImportFile.safeMutate(activeImportFacilityId.value, {
      file,
    });

    const entityNames = [
      ...getAllTranslations(
        currentPageSetup.value.templateWorksheetName,
      ).values(),
    ];

    const contents = await parseBulkFile(file, {
      entityNames,
    });

    if (!contents) {
      throw new $ck.utils.UserFriendlyError(
        t("cksetup.imports.import-into-table.sheetNotFoundErrorMessage", {
          name: entityNames[0],
        }),
      );
    }
    const { srcColumns, srcData } = contents;
    uploadedData.value = srcData;
    uploadedDataColumns.value = srcColumns;

    mappingSuggestions.value = await mapColumns(
      [...gridConfig.value.importableColumnsHash.values()],
      srcColumns,
      t,
    );

    // All columns are mapped
    if (!isMappingPending.value) {
      safeMapAndAppendData(srcData);
      return uploadedData.value;
    }
    return uploadedData.value;
  };

  const defaultAppendFn: AppendDataFn = (a, b, fields) => {
    let itemsLeft = [...b];
    const existingItems = !gridConfig.value?.uniqueKey
      ? a
      : a.map((prevItem) => {
          const existing = getDuplicatedItems(
            itemsLeft,
            prevItem,
            gridConfig.value!.uniqueKey,
          );

          if (!existing.length) {
            return prevItem;
          }

          const firstExistingRow = existing[0];

          itemsLeft = itemsLeft.filter((item) => item !== firstExistingRow);
          const result = {
            ...prevItem,
            // only replace with the first matching row, the duplicates are added later
            ...firstExistingRow,
          };
          result.$dirty = makeDirtyRecord(result, fields);
          return result;
        });

    const addedItems = itemsLeft.map((rawItem) => {
      const item = makeGridDataItem(rawItem);
      item.$dirty = makeDirtyRecord(item, fields);
      return item;
    });

    return [...existingItems, ...addedItems];
  };

  /**
   * Appends new data items to the existing data items.
   * The default behavior is to append the data to end of the existing data items.
   * But if appendDataFn is set, it will be used to append the data in a custom way.
   */
  const appendData = async (srcData: Partial<GridDataItemRaw>[]) => {
    if (!currentPageSetupId.value) return;

    const normalized = gridConfig.value
      ? normalizeDataItems(srcData, gridConfig.value)
      : srcData;

    const appendFn = currentPageSetup.value?.appendDataFn ?? defaultAppendFn;

    const mergedItems = await appendFn(
      dataItems.value,
      normalized,
      importableColumnsKeys.value,
    );
    dataItems.value = await prepareItemsForEdition(mergedItems);

    clearPageDraftSnapshot(currentPageSetupId.value);
    bulkImportStore.refreshUnsavedChanges();

    return dataItems.value;
  };

  const safeMapAndAppendData = async (
    srcData: UploadedData,
    suggestions: ColumnMappingSuggestion[] = mappingSuggestions.value,
  ) => {
    if (!currentPageSetup.value) {
      devConsole.error("No page setup available");
      return;
    }

    mappingSuggestions.value = suggestions;

    if (isMappingPending.value) {
      devConsole.error("Mapping is pending, cannot append data");
      return;
    }

    const formatOptions = toValue(gridConfig.value?.options.formatOptions);
    const mappedData = await mapUploadedData(srcData, mappingSuggestions.value);

    const transformedData = await transformDataItems(
      mappedData,
      gridConfig.value!,
      formatOptions,
      currentPageSetup.value?.transformDataItemFn,
    );

    return appendData(transformedData);
  };

  const detectConflicts = async () => {
    if (!gridConfig.value?.uniqueKey.length) return;

    // refresh the original data items, to check which items are existing vs new
    await currentPageSetup.value?.loadGridDataFn();

    const remainingItems = new Set(dataItems.value);

    const computedOriginalDataItems = await prepareItemsForEdition(
      clonePersistentItems(originalDataItems.value),
    );

    // task-lists do not show merge conflicts
    if (currentPageSetupId.value === "task-list") return;

    mergeConflicts.value = computedOriginalDataItems
      .map((existingItem, existingItemIndex) => {
        const duplicates = getDuplicatedItems(
          [...remainingItems],
          existingItem,
          gridConfig.value!.uniqueKey,
        );

        if (!duplicates.length) return;

        duplicates.forEach((dup) => remainingItems.delete(dup));

        const conflicts = duplicates.filter(
          (dup) =>
            identifyMismatchedFields(dup.$validatedData ?? dup, {
              fields: importableColumnsKeys.value,
              original: existingItem,
            }).length > 0,
        );

        if (!conflicts.length) return;

        return {
          existingItem,
          existingItemIndex,
          conflicts,
        };
      })
      .filter((item) => item !== undefined);
  };

  const assignCurrentResponseErrorRecord = () => {
    if (!currentPageSetup.value) return;

    const response = toValue(currentPageSetup.value.saveErrorResponse);
    if (!response) return;

    const responseErrors = parseResponseErrorRecord(response);
    if (!responseErrors?.length) return;

    responseErrors
      .map(({ indexOrId: index, propertyName, errors }) => ({
        index,
        ...(propertyName
          ? {
              fieldErrors: {
                [propertyName]: errors,
              },
            }
          : {
              formErrors: errors,
            }),
      }))
      .forEach(({ index, ...$inputDataErrors }) => {
        if (!dataItems.value[index]) return;

        dataItems.value.splice(index, 1, {
          ...dataItems.value[index],
          $inputDataErrors: $ck._.merge(
            dataItems.value[index].$inputDataErrors,
            $inputDataErrors,
          ),
        });
      });
  };

  /**
   * saves the given data using the provided save function.
   * used by conflict resolution where the data conflicts are solved.
   * @param data Saves
   * @returns
   */
  const saveResolvedData = async (
    data: GridDataItemRaw[],
    changes?: BulkImportChanges,
  ) => {
    if (!currentPageSetup.value || !gridConfig.value) {
      devConsole.error("No page setup available");
      return false;
    }

    try {
      const result = await currentPageSetup.value.saveGridDataFn(data, changes);
      if (result === undefined) {
        assignCurrentResponseErrorRecord();
        return false;
      }

      await currentPageSetup.value.loadGridDataFn();
      await restoreOriginal();

      await updateFacilityStatus();
      if (typeof result === "function") {
        // TODO: for now we replace the current data with the data with errors
        dataItems.value = [...result(dataItems.value)];
      }
      return true;
    } catch (error) {
      devConsole.error("error saving data", error);
    }
    return false;
  };

  /**
   * Saves the current data items using the provided save function.
   * Checks for conflicts before saving.
   */
  const save = async (reason?: string) => {
    try {
      await detectConflicts();

      if (hasMergeConflicts.value) {
        devConsole.warn("conflicts detected, cannot save");
        return false;
      }

      const changes = makeDataChangesPayload(
        dataItems.value,
        originalDataItems.value,
        gridConfig.value?.uniqueKey,
        reason,
      );

      const data = $ck._.cloneDeep(
        $ck._.compact(
          dataItems.value
            .map(getRawDataItem)
            .map(({ $validatedData }) => $validatedData),
        ),
      );
      return saveResolvedData(data, changes);
    } catch (error) {
      devConsole.error("error saving data", error);
    }
    return false;
  };

  const cancelLeave = () => {
    unsavedChangesState.value.confirmationLeaveRoute = undefined;
    unsavedChangesState.value.showLeaveConfirmation = false;
  };

  const confirmLeave = () => {
    const confirmationLeaveRoute =
      unsavedChangesState.value.confirmationLeaveRoute;

    restoreOriginal();

    if (confirmationLeaveRoute) {
      return router.push(confirmationLeaveRoute);
    }
  };

  const saveInMemory = () => {
    if (!currentPageSetupId.value) return;

    const changes = makeDraftSnapshot(
      dataItems.value,
      originalDataItems.value,
      gridConfig.value?.uniqueKey,
    );

    if (changes) {
      setPageDraftSnapshot(currentPageSetupId.value, changes);
    } else {
      clearPageDraftSnapshot(currentPageSetupId.value);
    }
  };

  const dataChanged = async (payload: GridChangeEvent) => {
    // handle only updates that mark the row as dirty
    // a row can be marked as modified without being dirty
    const rowHasChanges =
      payload.type === "added" || payload.type === "deleted" || payload.isDirty;

    if (rowHasChanges && currentPageSetupId.value) {
      // under any change we need to clear the in memory changes
      clearPageDraftSnapshot(currentPageSetupId.value);

      if (
        payload.type === "deleted" &&
        payload.dataItem.$original !== undefined
      ) {
        bulkImportStore.deletedItems.push(payload.dataItem);
      }
      bulkImportStore.refreshUnsavedChanges();
    }
  };

  return {
    currentPageSetupId,
    currentPageSetup,
    mappableColumns,
    pageTitle,
    pageHeading,
    gridConfig,
    dataItems,
    originalDataItems,
    validationSchema,
    templateFileName,
    hasDraftSnapshot,
    hasUnsavedChanges,
    isDataStale,
    isMappingPending,
    mappingSuggestions,
    uploadedData,
    uploadedDataColumns,
    hasMergeConflicts,
    mergeConflicts,
    showLeaveConfirmationDialog,
    load,
    restoreOriginal,
    downloadTemplate,
    uploadAndMap,
    safeMapAndAppendData,
    save,
    saveInMemory,
    saveResolvedData,
    cancelLeave,
    confirmLeave,
    dataChanged,
    reset: () => bulkImportStore.reset(),
  };
}
